import { HeaderCms, HeaderData, getFooter, getHeader, getHeaderData, getPageContent } from "./services";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";

import BNFooter from "../BNFooter/BNFooter";
import Breadcrumbs from "../Breadcrumbs/Breadcrumbs";

import ErrorBoundary from "../ErrorBoundary/ErrorBoundary";
import { ResolveDocument } from "./ResolveDocument";
import { SettingsContext } from "../../settingsContext";
import { BNBottomNavigation, BNBox, pushValuesToDataLayer } from "@bn-monorepo/js-packages";
import { UmbracoABTestModel, pushVirtualPageView } from "../../helpers/pushToDatalayer";
import zenscroll from "zenscroll";
import ResolveHeader from "../BNHeader/ResolveHeader";
import CmsContentWrapper from "./CmsContentWrapper";
import CTAButton from "./CTAButton";
import { useCmsManagedHead } from "./useCmsManagedHead";
import { escapedLocationHash } from "../../helpers/escapedHash";

interface Props {
  cmsContent?: any;
  isLoggedIn: boolean;
  header?: any;
  footer?: any;
}

const CmsContentContainer: React.FC<Props> = (props) => {
  const settings = useContext(SettingsContext);
  const { productSlug, isApp, env } = settings;
  const location = useLocation();
  const { pathname: path, search } = location;
  const navigate = useNavigate();
  const [header, setHeader] = useState<HeaderCms>(props.header);
  const [cmsContent, setCmsContent] = useState<any>(props.cmsContent);
  const [footer, setFooter] = useState<any>(props.footer);
  const [headerData, setHeaderData] = useState<HeaderData>({
    homeLink: { title: "", url: "" },
    bottomNavigationLinks: [],
    topNavigationLinks: [],
    strings: {
      account: "",
      jumpToNavigation: "",
      notification: "",
      bnLogoAltTextLoggedIn: "",
      darkModePrimaryText: "",
      darkModeSecondaryText: "",
    },
  });

  const isFirstLoadRef = useRef(true);

  const [hydrationReady, setHydrationReady] = useState(false);

  useEffect(() => {
    if (!settings.hideLogin) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      getHeaderData(path).then((headerData) => setHeaderData(headerData));
    }
    if (isFirstLoadRef.current && props.cmsContent) {
      const abTest = props.cmsContent.abTest as UmbracoABTestModel;
      pushVirtualPageView(props.cmsContent.meta?.title, "first_load", {
        currentVariant: props.cmsContent.path,
        abTest,
      });
      isFirstLoadRef.current = false;
      setHydrationReady(true);
    } else {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      getHeader(path, settings).then(setHeader);
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      getPageContent(path, search, navigate, settings, !isFirstLoadRef.current)
        .then(setCmsContent)
        .then(() => window.scrollTo(0, 0));
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      getFooter(path, settings).then(setFooter);
    }

    hashLinkScroll();
    isFirstLoadRef.current = false;
  }, [path, search, settings, location, props.cmsContent, navigate]);

  const resolveCanonicalUrl = () => {
    if (!cmsContent) {
      return null;
    }
    const { canonicalPath } = cmsContent;

    const canonical = settings.resolveUrl(canonicalPath);

    return `${window.location.origin}${canonical}`;
  };

  const strings = settings.localization;

  const cmsContentOnly = search?.match(/clean=1/);

  const resolveFooter = () => {
    if (cmsContentOnly || settings.isApp) {
      return null;
    }
    const footerType = cmsContent?.layoutOverrides?.footerType as "Hidden" | undefined;
    if (footerType === "Hidden") {
      return null;
    }

    return (
      <BNBox sx={{ marginTop: "auto" }}>
        <ErrorBoundary errorMesage={strings.general_errorboundary_message}>
          <BNFooter isApp={settings.isApp} loggedIn={settings.isAuthenticated} {...footer} />
        </ErrorBoundary>
      </BNBox>
    );
  };

  if (settings.cookieConsentWantsMarketing) {
    pushValuesToDataLayer(settings.utmCookie);
  }

  const canonicalUrl = resolveCanonicalUrl();

  useCmsManagedHead(cmsContent, canonicalUrl);

  return (
    <>
      <CmsContentWrapper hydrationReady={hydrationReady}>
        {
          <ResolveHeader
            header={header}
            headerData={headerData}
            cmsContent={cmsContent}
            cmsContentOnly={cmsContentOnly != null}
          />
        }
        {!cmsContentOnly && cmsContent?.documentType !== "campaign" && settings && !settings.isApp && (
          <Breadcrumbs list={cmsContent?.breadcrumbs} hide={cmsContent?.breadcrumbsHide} />
        )}
        <ErrorBoundary errorMesage={strings.general_errorboundary_message}>
          <BNBox
            sx={{
              marginBottom: settings.isApp ? { xs: "6rem", md: "0rem" } : undefined,
            }}
            id="MainContent"
            tabIndex={-1}
            data-cms-content-key={env !== "prod" ? cmsContent.contentKey?.replaceAll("-", "") : undefined}
          >
            <ResolveDocument cmsContent={cmsContent} settings={settings} />
          </BNBox>
        </ErrorBoundary>
        <CTAButton cmsContent={cmsContent} />
        {isApp && (
          <BNBottomNavigation
            isSpa={() => false}
            actionList={headerData.bottomNavigationLinks}
            shouldBeActive={(currentPath, _actionUrl, productName) => {
              switch (productName) {
                case "creditcard":
                  return currentPath.startsWith(productSlug.creditCard);
                case "loan":
                  return currentPath.startsWith(productSlug.loan);
                case "savingsaccount":
                  return currentPath.startsWith(productSlug.savingsAccount);
                case "insurance":
                  return currentPath.startsWith(productSlug.insurance);
              }
              return false;
            }}
            visibleOnAllWidths={isApp}
          />
        )}
        {resolveFooter()}
      </CmsContentWrapper>
    </>
  );
};

function hashLinkScroll() {
  const hashLink = escapedLocationHash();
  if (hashLink) {
    setTimeout(() => {
      const scrollToElement = document.querySelector(hashLink);
      if (scrollToElement) {
        zenscroll.to(scrollToElement, 0);
      }
    }, 500);
  }
}

export default CmsContentContainer;
